import React, { useState, useEffect } from 'react';
import { User, CreditCard, Bell, Shield, LogOut, Tags, CheckCircle, XCircle } from 'lucide-react';
import { Header } from '../components/Header';
import { billingAPI, statsAPI, authAPI, transactionsAPI } from '../api/client';
import { useCategories } from '../hooks/useCategories';
import { CategoryList } from '../components/CategoryList';
import { AddCategoryForm } from '../components/AddCategoryForm';
import { Transaction } from '../mocks/mockData';

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const [usageStats, setUsageStats] = useState<any>(null);
  const [monthlyIncome, setMonthlyIncome] = useState<string>('');
  const [isUpdatingIncome, setIsUpdatingIncome] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const { categories, addCategory, updateCategory, deleteCategory } = useCategories();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeStatus = params.get('stripe');
    const success = params.get('success');
    const cancelled = params.get('cancelled');

    const verifySubscription = async () => {
      try {
        // Wait a moment for the webhook to process
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const authResponse = await authAPI.getUser();
        if (authResponse.success && authResponse.data && authResponse.data.households.length > 0) {
          const householdId = authResponse.data.households[0].id;
          const statsResponse = await statsAPI.getStats(householdId);
          
          if (statsResponse.success && statsResponse.data) {
            if (statsResponse.data.subscription.plan === 'premium') {
              setNotification({ type: 'success', message: 'Subscription updated successfully!' });
            }
            // Update local state as well
            setSubscription(statsResponse.data.subscription);
          }
        }
      } catch (error) {
        console.error('Failed to verify subscription:', error);
      }
    };

    if (stripeStatus === 'success' || success === 'true') {
      verifySubscription();
      window.history.replaceState({}, '', window.location.pathname);
      
      const isMock = params.get('mock_payment') === 'true';
      const message = isMock 
        ? 'Mock payment successful! Premium features enabled.' 
        : 'Subscription updated successfully!';
        
      setNotification({ type: 'success', message });
      setTimeout(() => setNotification(null), 5000);
    } else if (stripeStatus === 'cancel' || cancelled === 'true') {
      setNotification({ type: 'error', message: 'Subscription update cancelled.' });
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setNotification(null), 5000);
    }
    
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      // Load user data and subscription using the proper API client
      const authResponse = await authAPI.getUser();
      
      if (authResponse.success && authResponse.data) {
        setUserData(authResponse.data.user);
        
        const { households } = authResponse.data;
        if (households && households.length > 0) {
          const householdId = households[0].id;
          
          // Load subscription and stats
          const statsResponse = await statsAPI.getStats(householdId);
          
          if (statsResponse.success && statsResponse.data) {
            setSubscription(statsResponse.data.subscription);
            setMonthlyIncome(statsResponse.data.monthlyIncome?.toString() || '');
            
            // Calculate usage stats
            const recentTransactions = statsResponse.data.recentTransactions || [];
            const categorySpending = statsResponse.data.categorySpending || [];
            
            setUsageStats({
              transactionsThisMonth: recentTransactions.length,
              totalSpent: statsResponse.data.totalSpent,
              categoriesUsed: categorySpending.filter(cat => cat.amount > 0).length
            });
          }

          // Load all transactions for category dropdowns
          const transactionsResponse = await transactionsAPI.getTransactions(householdId);
          if (transactionsResponse.success && transactionsResponse.data) {
            // Combine API transactions with local transactions
            const localTransactions = JSON.parse(localStorage.getItem('autobudgetai-transactions') || '[]');
            const allTransactions = [...localTransactions, ...transactionsResponse.data.transactions];
            setTransactions(allTransactions);
          }
        }
      }
    } catch (error: any) {
      if (error.message === 'Authentication required' || error.message?.includes('401')) {
        // Redirect to home if not authenticated
        // window.location.href = '/';
        return;
      }
      console.error('Failed to load user data:', error);
    }
  };

  const handleUpdateIncome = async () => {
    if (!userData) return;
    
    setIsUpdatingIncome(true);
    try {
      const authResponse = await authAPI.getUser();
      if (authResponse.success && authResponse.data && authResponse.data.households.length > 0) {
        const householdId = authResponse.data.households[0].id;
        
        // We need to use householdsAPI to update, but we need to add update method to client first
        // For now, we'll assume the API exists or we'll implement it
        // Since we modified the backend, we should update the client
        
        // Using householdsAPI to update
        const response = await householdsAPI.updateHousehold(householdId, {
          monthly_income: parseFloat(monthlyIncome)
        });
        
        if (!response.success) throw new Error(response.error || 'Failed to update income');
        
        // Reload data
        loadUserData();
      }
    } catch (error) {
      console.error('Failed to update income:', error);
      alert('Failed to update income');
    } finally {
      setIsUpdatingIncome(false);
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'categories', name: 'Categories', icon: Tags },
    { id: 'billing', name: 'Billing', icon: CreditCard },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'security', name: 'Security', icon: Shield },
  ];

  const handleManageSubscription = async () => {
    if (!userData) return;
    
    setIsLoading(true);
    try {
      // Find user's household using the proper API client
      const authResponse = await authAPI.getUser();
      
      if (!authResponse.success || !authResponse.data) {
        throw new Error('Authentication failed');
      }

      const { households } = authResponse.data;
      
      if (!households || households.length === 0) {
        throw new Error('No household found');
      }

      const response = await billingAPI.getCustomerPortal({
        householdId: households[0].id,
        returnUrl: window.location.href
      });

      if (response.success && response.data) {
        window.location.href = response.data.portalUrl;
      } else {
        throw new Error(response.error || 'Failed to open customer portal');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      alert(error instanceof Error ? error.message : 'Failed to open customer portal');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Profile Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={userData?.display_name || ''}
                  readOnly
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={userData?.email || ''}
                  readOnly
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                />
              </div>
            </div>
            
            <div className="pt-4 border-t dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Profile editing will be available in a future update.
              </p>
            </div>
          </div>
        );

      case 'categories':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Expense Categories</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Manage your expense categories to better organize your budget</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <AddCategoryForm 
                onAddCategory={(name, color, targetPercentage, limitAmount) => {
                  try {
                    addCategory(name, color, targetPercentage, limitAmount);
                  } catch (error) {
                    console.error('Failed to add category:', error);
                  }
                }} 
                totalBudget={monthlyIncome ? parseFloat(monthlyIncome) : undefined}
                totalAllocatedPercentage={categories.reduce((sum, cat) => sum + (cat.targetPercentage || 0), 0)}
                currentCategoryCount={categories.length}
                isPremium={subscription?.plan === 'premium'}
              />
              
              <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">Your Categories</h4>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{categories.length} categories</span>
                </div>
                
                <CategoryList
                  categories={categories}
                  onUpdate={(id, updates) => {
                    try {
                      updateCategory(id, updates);
                    } catch (error) {
                      console.error('Failed to update category:', error);
                    }
                  }}
                  onDelete={(id) => {
                    try {
                      deleteCategory(id);
                    } catch (error) {
                      console.error('Failed to delete category:', error);
                    }
                  }}
                  totalBudget={monthlyIncome ? parseFloat(monthlyIncome) : undefined}
                  transactions={transactions}
                />
              </div>
            </div>
          </div>
        );

      case 'billing':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Subscription & Billing</h3>
            
            {/* Monthly Income Setting */}
            <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-6">
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Monthly Income</h4>
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Total Monthly Income
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 dark:text-gray-400 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      value={monthlyIncome}
                      onChange={(e) => setMonthlyIncome(e.target.value)}
                      className="block w-full pl-7 pr-12 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Used to calculate category spending targets.
                  </p>
                </div>
                <button
                  onClick={handleUpdateIncome}
                  disabled={isUpdatingIncome}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors mb-[2px]"
                >
                  {isUpdatingIncome ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            {/* Current Plan */}
            <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white capitalize">
                    {subscription?.plan || 'Free'} Plan
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    {subscription?.plan === 'free' 
                      ? 'You are currently on the free plan'
                      : 'Your premium subscription is active'
                    }
                  </p>
                </div>
                <div className="text-right">
                  {subscription?.plan === 'free' ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                      Free
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                      Premium
                    </span>
                  )}
                </div>
              </div>
              
              {subscription?.plan === 'free' ? (
                <div className="mt-4 pt-4 border-t dark:border-gray-700">
                  <button
                    onClick={() => window.location.href = '/pricing'}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    Upgrade to Premium
                  </button>
                </div>
              ) : (
                <div className="mt-4 pt-4 border-t dark:border-gray-700">
                  <button
                    onClick={handleManageSubscription}
                    disabled={isLoading}
                    className="inline-flex items-center px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white rounded-md text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Loading...
                      </>
                    ) : (
                      'Manage Billing'
                    )}
                  </button>
                </div>
              )}
            </div>
            
            {/* Billing History */}
            {subscription?.plan === 'premium' && (
              <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-6">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Billing History</h4>
                <p className="text-gray-600 dark:text-gray-400">No billing history available.</p>
              </div>
            )}
            
            {/* Usage Statistics */}
            <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-6">
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Usage Statistics</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {usageStats?.transactionsThisMonth || 0}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Transactions this month</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {usageStats ? formatCurrency(usageStats.totalSpent) : '$0.00'}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total spent</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {usageStats?.categoriesUsed || 0}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Categories used</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Notification Preferences</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Email Notifications</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Receive budget alerts and weekly summaries</p>
                </div>
                <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 dark:bg-gray-600">
                  <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6" />
                </button>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Budget Alerts</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Get notified when you approach your budget limits</p>
                </div>
                <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600">
                  <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-1" />
                </button>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Marketing Emails</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Receive updates about new features and tips</p>
                </div>
                <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 dark:bg-gray-600">
                  <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6" />
                </button>
              </div>
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Security Settings</h3>
            
            <div className="space-y-4">
              <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-6">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Password</h4>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  You are authenticated through your YouWare account. Password management is handled by YouWare.
                </p>
                <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium">
                  Manage account on YouWare
                </button>
              </div>
              
              <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-6">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Data Export</h4>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Download all your data in a portable format
                </p>
                <button className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                  Export Data
                </button>
              </div>
              
              <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-6 border-red-200 dark:border-red-900/50">
                <h4 className="text-lg font-medium text-red-900 dark:text-red-400 mb-2">Delete Account</h4>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Permanently delete your account and all associated data
                </p>
                <button className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors">
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {notification && (
          <div className={`mb-6 p-4 rounded-md ${
            notification.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400'
          }`}>
            <div className="flex items-center">
              {notification.type === 'success' ? (
                <CheckCircle className="h-5 w-5 mr-2" />
              ) : (
                <XCircle className="h-5 w-5 mr-2" />
              )}
              {notification.message}
            </div>
          </div>
        )}

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage your account settings and preferences</p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <nav className="space-y-2">
              {tabs.map(({ id, name, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                    activeTab === id
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-r-2 border-blue-600 dark:border-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {name}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 shadow-sm p-6">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}